/**
 * Scroll-Engine für das Scrollytelling.
 *
 * Misst pro Kapitel, wie weit der Anker (Bildschirmmitte, mobil weiter unten)
 * durch den Textblock gewandert ist, und schreibt das als `--l` und `--vis`
 * auf die zugehörige Szene der Bühne. Szenen mit JS-Anteil (Zähler, Tippen)
 * hängen sich per `useFrame` an. React rendert nur, wenn das aktive Kapitel
 * wechselt.
 *
 * Modus `pin` (Version 2): Jedes Kapitel ist eine hohe Section mit einer
 * sticky Szene. `--l` läuft von 0 (Section oben am Viewport) bis 1 (Section
 * unten am Viewport), also genau über die Zeit, in der die Szene steht.
 */

import PropTypes from 'prop-types'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import { clamp, seg } from './motion'

const MOBILE_BREAKPOINT = 1024

export const anchorFor = () =>
  window.innerHeight * (window.innerWidth < MOBILE_BREAKPOINT ? 0.78 : 0.5)

export function useScrollStory(count, { mode = 'anchor' } = {}) {
  const chapterRefs = useRef([])
  const sceneRefs = useRef([])
  const stepRefs = useRef({})
  const storyRef = useRef(null)
  const progressRef = useRef(null)
  const subscribers = useRef(new Map())
  const lastL = useRef([])
  const [active, setActive] = useState(0)

  const subscribe = useCallback((index, cb) => {
    if (!subscribers.current.has(index))
      subscribers.current.set(index, new Set())
    const set = subscribers.current.get(index)
    set.add(cb)
    cb(lastL.current[index] ?? 0)
    return () => set.delete(cb)
  }, [])

  useEffect(() => {
    let raf = 0
    const fade = () => window.innerHeight * 0.3

    const update = () => {
      raf = 0
      const anchor = anchorFor()
      const f = fade()
      let best = 0
      let bestVis = -1

      for (let i = 0; i < count; i++) {
        const chapter = chapterRefs.current[i]
        const scene = sceneRefs.current[i]
        if (!chapter || !scene) continue
        const r = chapter.getBoundingClientRect()
        const vh = window.innerHeight
        const pinned = mode === 'pin'
        const l = pinned
          ? clamp(-r.top / Math.max(1, r.height - vh))
          : clamp((anchor - r.top) / r.height)
        // Im Pin-Modus gewinnt fürs aktive Kapitel die Section unter der Bildschirmmitte
        const onScreen = r.bottom > 0 && r.top < vh
        const vis = pinned
          ? onScreen
            ? r.top <= vh / 2
              ? 1
              : 0.5
            : 0
          : clamp(
              Math.min(
                (anchor - r.top) / f + 0.5,
                (r.bottom - anchor) / f + 0.5
              )
            )
        if (vis >= bestVis && vis > 0) {
          best = i
          bestVis = vis
        }
        scene.style.setProperty('--vis', vis.toFixed(3))
        scene.style.visibility = vis > 0 ? 'visible' : 'hidden'
        if (vis === 0 || lastL.current[i] === l) continue
        lastL.current[i] = l
        scene.style.setProperty('--l', l.toFixed(4))
        subscribers.current.get(i)?.forEach((cb) => cb(l))
      }

      const vh = window.innerHeight
      Object.values(stepRefs.current).forEach((el) => {
        if (!el) return
        const r = el.getBoundingClientRect()
        const d = Math.abs(r.top + r.height / 2 - anchor) / (vh * 0.55)
        el.style.setProperty('--s', (1 - clamp(d)).toFixed(3))
      })

      if (storyRef.current && progressRef.current) {
        const r = storyRef.current.getBoundingClientRect()
        const p = clamp((anchor - r.top) / r.height)
        progressRef.current.style.setProperty('--story', p.toFixed(4))
      }

      setActive(best)
    }

    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(update)
    }

    update()
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
    }
  }, [count, mode])

  const scrollToChapter = useCallback(
    (index) => {
      const el = chapterRefs.current[index]
      if (!el) return
      const top = el.getBoundingClientRect().top + window.scrollY
      // Anker auf die Mitte des ersten Schritts setzen, im Pin-Modus auf den Kapitelanfang
      const target =
        mode === 'pin' ? top : top - anchorFor() + window.innerHeight * 0.45
      const reduce = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches
      window.scrollTo({ top: target, behavior: reduce ? 'auto' : 'smooth' })
    },
    [mode]
  )

  return {
    active,
    chapterRefs,
    sceneRefs,
    stepRefs,
    storyRef,
    progressRef,
    subscribe,
    scrollToChapter
  }
}

const SceneContext = createContext({ index: -1, subscribe: () => () => {} })

export function SceneProvider({ index, subscribe, children }) {
  const value = useMemo(() => ({ index, subscribe }), [index, subscribe])
  return <SceneContext.Provider value={value}>{children}</SceneContext.Provider>
}

SceneProvider.propTypes = {
  index: PropTypes.number.isRequired,
  subscribe: PropTypes.func.isRequired,
  children: PropTypes.node
}

/** Ruft `cb(l)` bei jeder Fortschrittsänderung der eigenen Szene auf. */
export function useFrame(cb) {
  const { index, subscribe } = useContext(SceneContext)
  const ref = useRef(cb)
  ref.current = cb
  useEffect(() => subscribe(index, (l) => ref.current(l)), [index, subscribe])
}

/**
 * Bildet den Bereich `at` der Elternszene auf 0..1 ab, für CSS (`--l`) und
 * `useFrame`. So laufen Bausteine aus Version 1 unverändert in einem Teil
 * einer längeren Szene.
 */
export function SubRange({ at, className, style, children }) {
  const parent = useContext(SceneContext)
  const [a, b] = at
  const subscribe = useCallback(
    (index, cb) => parent.subscribe(index, (l) => cb(seg(l, a, b))),
    [parent, a, b]
  )
  const value = useMemo(
    () => ({ index: parent.index, subscribe }),
    [parent.index, subscribe]
  )
  return (
    <div className={className} style={{ '--L': 'var(--l)', ...style }}>
      <div
        className='sty-subrange'
        style={{ '--l': `clamp(0, (var(--L) - ${a}) / ${b - a}, 1)` }}
      >
        <SceneContext.Provider value={value}>{children}</SceneContext.Provider>
      </div>
    </div>
  )
}

SubRange.propTypes = {
  at: PropTypes.arrayOf(PropTypes.number).isRequired,
  className: PropTypes.string,
  style: PropTypes.object,
  children: PropTypes.node
}
